'use client'

import dynamic from 'next/dynamic'
import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import type { ProductPageType } from '../../product-page'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'
import { STICKY_ADD_TO_CART_FLAG } from '../product-page.constants'

const ProductStickyActionBar = dynamic(() => import('./product-sticky-action-bar'), { ssr: false })

type Props = {
  product: ProductPageType
  children: ReactNode
  actionComponent: ReactNode
}

const ProductStickyActionBarObserver = ({ product, children, actionComponent }: Props) => {
  const containerRef = useRef(null)
  const hiddenByBottom = useRef(false)
  const [showActionBar, setShowActionBar] = useState(false)
  const { enabled } = useFeatureFlag(STICKY_ADD_TO_CART_FLAG)

  useEffect(() => {
    if (!enabled) {
      return
    }

    const container = containerRef.current

    if (!container) return
    const observer = new IntersectionObserver(([entry]) => {
      hiddenByBottom.current = false
      setShowActionBar(!entry.isIntersecting && entry.boundingClientRect.top < 0)
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [enabled])

  useEffect(() => {
    const handleScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 1

      if (atBottom) {
        setShowActionBar(false)
      } else if (hiddenByBottom.current) {
        setShowActionBar(true)
      }

      hiddenByBottom.current = atBottom
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      {enabled && (
        <ProductStickyActionBar product={product} isVisible={showActionBar}>
          {actionComponent}
        </ProductStickyActionBar>
      )}
      <div ref={containerRef}>{children}</div>
    </>
  )
}

export default memo(ProductStickyActionBarObserver)
